import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, HeartHandshake, ShieldCheck, Info, Signpost, HandHeart } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { PageHeader, SectionHeader, RichSection } from '../components/Shared';
import { useRichSection } from '../lib/pageContent';
import { fileUrl } from '../lib/api';
import { setPageMeta } from '../lib/seo';

// Information-only Donate page (Phase 123). The contribution rules below are
// the Ontario Municipal Elections Act rules published by the Province for the
// 2026 municipal elections, with links to the official pages. Payment
// instructions are NOT invented: the campaign fills the "How to contribute"
// section in Admin > Page Content > Donate (text sections).
const SOURCES = [
  { label: '2026 Candidates\' Guide: Campaign finances (Ontario.ca)', url: 'https://www.ontario.ca/document/2026-candidates-guide-ontario-municipal-council-and-school-board-elections/campaign' },
  { label: '2026 Voters\' Guide: Contributions (Ontario.ca)', url: 'https://www.ontario.ca/document/2026-voters-guide-ontario-municipal-council-and-school-board-elections/contributions' },
  { label: 'Municipal Elections Act, 1996 (Ontario e-Laws)', url: 'https://www.ontario.ca/laws/statute/96m32' },
];

const RULES = [
  { title: 'Who can contribute', body: 'Only individuals who normally live in Ontario. Corporations, trade unions, clubs, associations and ratepayer groups cannot contribute to a municipal candidate.' },
  { title: 'Limits', body: 'Up to $1,200 to one candidate, and no more than $5,000 in total to candidates running for the same council. Goods and services you provide count toward these limits.' },
  { title: 'Cash', body: 'Cash is accepted only for contributions of $25 or less. Anything over $25 must come by cheque, money order or an electronic method that clearly identifies who gave it.' },
  { title: 'Anonymous contributions', body: 'Not permitted, except cash of $25 or less collected at a fundraising event. Anonymous amounts over $25 must be turned over to the City Clerk.' },
  { title: 'Receipts and records', body: 'The campaign issues a receipt for every contribution and keeps a record of everyone whose contributions total more than $100. Contributor names for amounts over $100 appear in the public financial statement filed with the City.' },
  { title: 'Tax receipts', body: 'Municipal campaign contributions are not eligible for income tax credits. Ontario municipalities may run their own rebate programs; the campaign will tell you if one applies in Kawartha Lakes.' },
  { title: 'When', body: 'Contributions can only be accepted after the candidate has filed a nomination and before the end of the campaign period. Anything that cannot be accepted is returned or paid to the Clerk.' },
];

export default function Donate() {
  const howToGive = useRichSection('donate-sections.how_to_give');
  useEffect(() => {
    setPageMeta({ title: 'Donate | Steve Lesperance for Ward 8', description: 'How to contribute to the Steve Lesperance Ward 8 campaign under the Ontario municipal election rules.', url: window.location.origin + '/donate' });
  }, []);

  return (
    <div data-testid="donate-page">
      <PageHeader pageKey="donate" kicker="SUPPORT THE CAMPAIGN" title="Donate" subtitle="Every contribution follows the Ontario municipal election rules below. Thank you for helping bring accountability to City Hall." image={fileUrl('/api/uploads/vote-steve-hero.webp')} />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12 grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          <section data-testid="donate-how-to-give">
            <SectionHeader kicker="HOW TO CONTRIBUTE" title="Making a contribution" />
            <RichSection id="donate-sections.how_to_give">
              {!howToGive && (
                <Card className="border-amber-200 bg-amber-50" data-testid="donate-details-pending"><CardContent className="p-5">
                  <p className="font-bold text-amber-900 inline-flex items-center gap-2"><Info className="h-5 w-5" /> Payment details are being finalised</p>
                  <p className="text-sm text-amber-900/90 mt-1.5">The campaign will publish exactly how to contribute (and to whom) here. Until then, please <Link to="/contact" className="font-bold underline underline-offset-2">contact the campaign</Link> and we will arrange it with you directly, following the rules on this page.</p>
                </CardContent></Card>
              )}
            </RichSection>
          </section>

          <section data-testid="donate-rules">
            <SectionHeader kicker="THE RULES" title="Ontario municipal contribution rules, 2026" dek="Set by the Province in the Municipal Elections Act. The campaign follows them to the letter." />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {RULES.map((r) => (
                <Card key={r.title} data-testid="donate-rule-card"><CardContent className="p-5">
                  <p className="font-serif font-bold text-[#1a2744]">{r.title}</p>
                  <p className="text-sm text-slate-700 mt-1.5 leading-relaxed">{r.body}</p>
                </CardContent></Card>
              ))}
            </div>
            <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600" data-testid="donate-sources">
              <p className="font-bold text-[#1a2744] mb-1.5">Official sources</p>
              <ul className="space-y-1">
                {SOURCES.map((s) => <li key={s.url}><a href={s.url} target="_blank" rel="noreferrer" className="underline underline-offset-2 inline-flex items-center gap-1">{s.label} <ExternalLink className="h-3 w-3" /></a></li>)}
              </ul>
              <p className="mt-2 text-xs text-slate-500">Figures checked against the Province's 2026 guides. If the Province changes a limit, this page will be updated.</p>
            </div>
          </section>

          <RichSection id="donate-sections.thank_you" />
        </div>

        <aside className="space-y-4">
          <Card className="bg-[#1a2744] border-0"><CardContent className="p-5">
            <p className="text-[#C9A84C] text-xs font-black uppercase tracking-widest inline-flex items-center gap-1.5"><HeartHandshake className="h-4 w-4" /> Before you give</p>
            <ul className="mt-2 text-sm text-slate-200 space-y-1.5 list-disc pl-4">
              <li>You live in Ontario.</li>
              <li>You are giving as an individual, not a business or group.</li>
              <li>Total to this campaign stays at or under $1,200.</li>
              <li>Over $25? Use cheque, money order or a traceable electronic payment.</li>
            </ul>
          </CardContent></Card>
          <Card><CardContent className="p-5">
            <p className="font-bold text-[#1a2744] inline-flex items-center gap-2"><ShieldCheck className="h-5 w-5" /> Your details</p>
            <p className="text-sm text-slate-700 mt-1.5">Contributor names and amounts over $100 are part of the public financial statement required by law. Contact details are never published.</p>
          </CardContent></Card>
          <Card><CardContent className="p-5">
            <p className="font-bold text-[#1a2744]">Other ways to help</p>
            <div className="mt-3 flex flex-col gap-2">
              <Button asChild variant="outline" className="h-11 justify-start border-[#1a2744] text-[#1a2744] font-bold bg-white btn-press" data-testid="donate-aside-sign"><Link to="/request-a-sign"><Signpost className="h-4 w-4 mr-2" /> Request a lawn sign</Link></Button>
              <Button asChild variant="outline" className="h-11 justify-start border-[#1a2744] text-[#1a2744] font-bold bg-white btn-press" data-testid="donate-aside-volunteer"><Link to="/volunteer"><HandHeart className="h-4 w-4 mr-2" /> Volunteer</Link></Button>
            </div>
          </CardContent></Card>
        </aside>
      </div>
    </div>
  );
}
